import { Stack, HStack, Span, Box, Separator, Flex, Text, VStack } from '@chakra-ui/react';
import { DiscountMode, Res, SoldProductDTO, SoldProductType, TicketLineDTO, TicketLineStatus } from 'core/api';
import { SvgIcon } from 'core/ui-next';
import React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';

const SERVICE_CHARGE_LINE = -5000;
const formatMoney = UI.Format.asMoney;

function getLineCashDiscountCode(line: TicketLineDTO): string {
	return line.AppliedDiscountMode === DiscountMode.Cash ? line.AppliedDiscountCode : '';
}

function getLineDiscountPercentageCode(line: TicketLineDTO): string {
	const codes: string[] = [];

	if (line.AppliedOfferCode)
		codes.push(line.AppliedOfferCode);

	if (line.AppliedDiscountMode === DiscountMode.Rate)
		codes.push(line.AppliedDiscountCode);

	return codes.join('/');
}

function getStatusIcon(line: TicketLineDTO): React.ReactNode | null {

	if (line.Id === SERVICE_CHARGE_LINE)
		return null;

	const statusImage = (line.HasBeenRequested ? Images.LineStatusSent : (line.Status === TicketLineStatus.New ? Images.LineStatusCommand : Images.LineStatusMarch));

	return <SvgIcon src={statusImage} h='3' w='3' />;
}

function formatTotalAmount(line: TicketLineDTO): string {
	if (line.OwnerId && line.TotalAmount === 0)
		return Res.Web.TICKET_LINES_CONTROL_MENU_ITEM_LBL();

	const formattedAmount = formatMoney(line.TotalAmount);
	return line.OwnerId ? '+' + formattedAmount : formattedAmount;
}

function getSaleFormatName(soldProducts: SoldProductDTO[]): string {
	return soldProducts.length > 1 ? `${soldProducts[0].SaleFormatName} (x${soldProducts.length})` : soldProducts[0].SaleFormatName;
}

//Aquí componemos el bloque para las líneas que en comandera tengan detalles, como notas, añadidos, etc.
//De forma que se muestren en la parte inferior de la línea algunos detalles.
function ComposeLineDetails({ children, detailsPreview }: { children: React.ReactNode, detailsPreview: boolean }): React.ReactNode {
	return (detailsPreview ?
		<HStack fontSize={'xs'} fontWeight={'semibold'} textWrap={'nowrap'}>
			{children}
		</HStack>
		: <Box fontSize={'xs'} fontWeight={'semibold'}>
			{children}
		</Box>
	);
}

function getLineDetailBlocks(line: TicketLineDTO, numberOfLinesToShow?: number, showEditButtons?: boolean): React.ReactNode | null {
	const blocks: React.ReactNode[] = [];
	const detailsPreview = !(!numberOfLinesToShow || numberOfLinesToShow <= 0);

	if (line.Product.Size.Id > 0) {
		blocks.push(
			<ComposeLineDetails detailsPreview={detailsPreview}>
				<>
					<Text>
						{Res.Web.COMPOSE_PRODUCT_SIZE_LBL()}
						<Span fontWeight={'normal'}>
							{' - '} {line.Product.Size.Name}
						</Span>
					</Text>
					<Text>
						{Res.Web.COMPOSE_PRODUCT_COLOR_LBL()}
						<Span fontWeight={'normal'}>
							{' - '} {line.Product.Color.Name}
						</Span>
					</Text>
				</>
			</ComposeLineDetails>
		);
	}
	if (line.Addins && line.Addins.length > 0) {
		blocks.push(
			<ComposeLineDetails detailsPreview={detailsPreview}>
				{line.Addins
					.groupBy(x => x.SaleFormatId)
					.map((addins, index) => (
						<Text key={index}>
							{Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITH_LBL()}
							<Span fontWeight={'normal'}>
								{' - '} {getSaleFormatName(addins.value)}
							</Span>
						</Text>
					))}
			</ComposeLineDetails>
		);
	}
	if (line.RemovedIngredients && line.RemovedIngredients.length > 0) {
		blocks.push(
			<ComposeLineDetails detailsPreview={detailsPreview}>
				{line.RemovedIngredients.map((ingredient, index) => (
					<Text key={index}>
						{Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITHOUT_LBL()}
						<Span fontWeight={'normal'}>
							{' - '} {ingredient.Style.Text}
						</Span>
					</Text>
				))}
			</ComposeLineDetails>
		);
	}
	if (line.Product.Lot && line.Product.Lot.Id > 0) {
		blocks.push(
			<ComposeLineDetails detailsPreview={detailsPreview}>
				{[line.Product.Lot, ...line.Addins.map(x => x.Lot)].filter(x => x.Id > 0).map((lot, index) => (
					<Text key={index}>
						{Res.Web.TICKET_LINES_CONTROL_PRODUCT_LOT_LBL(lot.Number)}
					</Text>
				))}
			</ComposeLineDetails>
		);
	}
	if (line.Notes && line.Notes.length > 0) {
		blocks.push(
			<ComposeLineDetails detailsPreview={detailsPreview}>
				{line.Notes.map((note, index) => (
					<Text key={index}>
						{Res.Web.TICKET_LINES_CONTROL_NOTE_LBL()}
						<Span fontWeight={'normal'}>
							{' - '} {note}
						</Span>
					</Text>
				))}
			</ComposeLineDetails>
		);
	}
	if (line.HasCashDiscount) {
		blocks.push(
			<ComposeLineDetails detailsPreview={detailsPreview}>
				<Text>
					{Res.Web.TICKET_LINE_CONTROL_DISCOUNT_LBL()} {getLineCashDiscountCode(line)}
					<Span fontWeight={'normal'}>
						{' - '} {formatMoney(line.CashDiscount)}
					</Span>
				</Text>
			</ComposeLineDetails>
		);
	}
	if (line.HasDiscountPercentage) {
		blocks.push(
			<ComposeLineDetails detailsPreview={detailsPreview}>
				<Text>
					{Res.Web.TICKET_LINE_CONTROL_DISCOUNT_LBL()} {getLineDiscountPercentageCode(line)}
					<Span fontWeight={'normal'}>
						{' - '} {UI.Format.asPercentage(line.DiscountPercentage)} ({formatMoney(line.DiscountPercentageInCash)})
					</Span>
				</Text>
			</ComposeLineDetails>
		);
	}

	if (blocks.length === 0) return null;

	return (detailsPreview ?
		<HStack paddingLeft={'11'} gap={'1.5'} w={'fit-content'} color={{ base: 'neutral.500', _dark: 'neutral.400' }} width={showEditButtons ? '70%' : 'full'} overflow={'hidden'}>
			{blocks.map((block, idx) => (
				<React.Fragment key={idx}>
					{block}
					{idx < blocks.length - 1 && (
						<Separator orientation='vertical' borderColor={{ base: 'neutral.300', _dark: 'white' }} height={'full'} />
					)}
				</React.Fragment>
			))}
		</HStack>
		: <Stack paddingLeft={'20'} gap={'0.5'} w={'fit-content'} color={{ base: 'neutral.500', _dark: 'neutral.400' }} maxW={'full'}>
			{blocks.map((block, idx) => (
				<React.Fragment key={idx}>
					{block}
					{idx < blocks.length - 1 && (
						<Separator w={'full'} borderColor={{ base: 'neutral.300', _dark: 'white' }} />
					)}
				</React.Fragment>
			))}
		</Stack>
	);
}

type TicketLineProps = {
	line: TicketLineDTO;
	selectedLine: Nullable<TicketLineDTO>;
	onSelect: (line: Nullable<TicketLineDTO>) => void;
	onEdit?: (line: TicketLineDTO) => void;
	onDelete?: (lineId: TicketLineDTO) => void;
	hasEditionInLine?: boolean;
	showTicketStatus?: boolean;
	expandedTicket?: boolean;
	someLineHasSeatNumber?: boolean;
	numberOfLinesToShow?: number;
};

export const TicketLines = React.forwardRef<HTMLDivElement, TicketLineProps>(
	({ line, selectedLine, onSelect, onEdit, onDelete, hasEditionInLine, showTicketStatus = true, expandedTicket = true, someLineHasSeatNumber, numberOfLinesToShow }, ref) => {
		const isSelected = line.Id === selectedLine?.Id;
		const isMenuItem = line.Product.Type === SoldProductType.MenuItem;
		const isServiceCharge = line.Id === SERVICE_CHARGE_LINE;
		const [showControlButtons, setShowControlButtons] = React.useState(false);

		React.useEffect(() => {
			if (!isSelected)
				setShowControlButtons(false);
		}, [isSelected]);

		const onControlLine = () => {

			if (showControlButtons) {
				setShowControlButtons(false);
				return;
			}

			onSelect(line);
			setShowControlButtons(true);
		};

		const showEditButtons = isSelected && hasEditionInLine && showControlButtons;

		//Este flag se usa cuando estamos en comanderas en la pantalla de ventas y vemos las líneas de tickets
		//Con esto controlamos que diseño poner si tienen detalles de añadidos, notas, ingredientes, etc.
		//Para ajustar los tamaños y disposición de los elementos.
		const haveNotDetailsToShow = getLineDetailBlocks(line) === null || (!numberOfLinesToShow || numberOfLinesToShow <= 0);

		return (
			<Stack
				key={line.Id}
				data-line-id={line.Id}
				ref={ref}
				w={'full'}
				paddingX={'2'}
				paddingY={haveNotDetailsToShow ? '2' : '0'}
				border={'1px solid'}
				borderColor={{ base: isSelected ? 'blue.200' : 'white', _dark: isSelected ? 'blue.700' : 'neutral.700' }}
				borderRadius={'md'}
				gap={haveNotDetailsToShow ? '2' : '0.5'}
				onClick={() => onControlLine()}
				bg={{ base: isSelected ? 'blue.100' : 'white', _dark: isSelected ? 'blue.700' : 'neutral.800' }}
				color={{ base: 'neutral.800', _dark: 'white' }}
				cursor={'pointer'}
				lineHeight={'120%'}
			>
				<Flex justify="space-between" align="center" height={haveNotDetailsToShow ? '6' : '5'}>
					<HStack gap={'2'} height={'full'}>
						{(!numberOfLinesToShow || numberOfLinesToShow <= 0) &&
							<Box width={'3'}>
								{showTicketStatus && getStatusIcon(line)}
							</Box>
						}
						<Text display={'flex'} justifyContent={'flex-end'} minWidth={'11'} fontSize={'sm'}>{UI.Format.asShortQuantity(line.Quantity)}</Text>
						<Text fontSize={'sm'} lineClamp={numberOfLinesToShow ? 1 : 2} overflowWrap={'anywhere'}>{line.Product.SaleFormatName}</Text>
					</HStack>
					{showEditButtons ?
						<HStack width={someLineHasSeatNumber ? '40' : '34'}>
							<HStack gap={'2'} justifyContent={'flex-end'} width={someLineHasSeatNumber ? '40' : '35.5'} position={'relative'} top={haveNotDetailsToShow ? '0' : '2'} right={'-1'} overflow={'hidden'}>
								<VStack
									gap={'0.5'}
									padding={'0.75'}
									width={'8'}
									height={'8'}
									alignItems={'center'}
									justifyContent={'center'}
									bg={{ base: 'red.700', _dark: 'red.400' }}
									borderRadius={'md'}
									color={'white'}
									onClick={(e) => {
										e.stopPropagation();
										onDelete?.(line);
									}}
									cursor={'pointer'}
								>
									<SvgIcon src={Images.TrashCan} w='4' h='4' />
								</VStack>
								<VStack
									gap={'0.5'}
									padding={'0.75'}
									width={'14'}
									height={'8'}
									alignItems={'center'}
									justifyContent={'center'}
									bg={'primary'}
									borderRadius={'md'}
									color={'white'}
									onClick={(e) => {
										e.stopPropagation();
										onEdit?.(line);
									}}
									cursor={'pointer'}
								>
									<SvgIcon src={Images.Edit} w='4' h='4' />
								</VStack>
							</HStack>
						</HStack>
						:
						<HStack height={'full'}>
							<Text display={'flex'} justifyContent={'flex-end'} width={'16'} fontSize={'sm'}>{isServiceCharge || isMenuItem ? ' ' : formatMoney(line.UnitPrice)}</Text>
							<Text display={'flex'} justifyContent={'flex-end'} width={'16'} fontSize={'sm'}>{formatTotalAmount(line)}</Text>
							{someLineHasSeatNumber ?
								line.SeatNumber ?
									<Box fontSize={'2xs'} fontWeight={'normal'} w={'4'} h={'4'} display={'flex'} position={'relative'} top={haveNotDetailsToShow ? '-1' : '0'} justifyContent={'center'} alignItems={'center'} color={{ base: 'secondary', _dark: 'white' }} border={'1px solid'} borderColor={{ base: 'secondary', _dark: 'white' }} borderRadius={'full'}>
										<Span>
											{line.SeatNumber}
										</Span>
									</Box>
									:
									<Span w={'4'}></Span>
								: null
							}
						</HStack>
					}
				</Flex>
				{(!numberOfLinesToShow || numberOfLinesToShow <= 0) && line.PreparationOrderName && line.UserName && expandedTicket &&
					<Stack paddingLeft={'18'} gap={'2'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>
						<HStack gap={'1.5'} fontSize={'xs'}>
							<HStack gap={'1.5'}>
								<SvgIcon src={Images.PlateType} w='4' h='4' />
								<span>{line.PreparationOrderName}</span>
							</HStack>
							<HStack gap={'1.5'}>
								<SvgIcon src={Images.User} w='4' h='4' />
								<span>{line.UserName}</span>
							</HStack>
						</HStack>
					</Stack>
				}
				{getLineDetailBlocks(line, numberOfLinesToShow, showEditButtons)}
			</Stack>
		);
	}
);